import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { toArucoPicture } from '@modules/aruco/adapters/aruco-picture.adapter';
import { PictureArucoComponent } from '@modules/aruco/components/picture-aruco/picture-aruco.component';
import { ArucoSticker } from '@modules/aruco/models/aruco-sticker';
import { ArucoDetectionService } from '@modules/aruco/services/aruco-detection.service';
import { SequenceMediaComponent } from '@modules/sequence/sequence-media/sequence-media.component';
import { MediaPickerComponent } from '@modules/shared/media-picker/components/media-picker/media-picker.component';
import { MediaListGroup } from '@modules/shared/media-picker/models/media-list-group';
import { MediaListItem } from '@modules/shared/media-picker/models/media-list-item';
import { MediaPickerSelection } from '@modules/shared/media-picker/models/media-picker-selection';
import { Media } from '@pixacare/domain';
import { TuiButton, TuiDialogContext, TuiNotificationService } from '@taiga-ui/core';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { catchError, EMPTY } from 'rxjs';
import { toPictureProviderOutput } from '../../adapters/picture-provider.adapter';
import {
  PictureProviderModuleComponentInputs,
  PictureProviderModuleComponentOutputs,
} from '../../models/picture-provider-module-component';
import { PictureProviderService } from '../../services/picture-provider.service';

@Component({
  selector: 'pxc-picture-provider',
  imports: [
    MediaPickerComponent,
    PictureArucoComponent,
    SequenceMediaComponent,
    TranslocoDirective,
    TuiButton,
    TuiButtonLoading,
  ],
  providers: [ArucoDetectionService, PictureProviderService],
  templateUrl: './picture-provider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PictureProviderComponent {
  private readonly context =
    inject<TuiDialogContext<PictureProviderModuleComponentOutputs | null, PictureProviderModuleComponentInputs>>(
      POLYMORPHEUS_CONTEXT,
    );
  private readonly translocoService = inject(TranslocoService);
  private readonly arucoDetectionService = inject(ArucoDetectionService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(PictureProviderService);

  protected readonly isItemSelectable = this.service.isItemSelectable;
  protected readonly validators = this.service.validators;

  protected readonly subtitle = this.translocoService.translate(
    'Veuillez choisir une photo sur laquelle le sticker est détecté',
  );
  protected readonly confirmLabel = this.translocoService.translate('Suivant');

  private readonly initialInputs = this.context.data?.woundtrackInputs ?? [];
  protected readonly isConfirming = signal(false);

  protected readonly groups = computed<MediaListGroup[]>(() =>
    this.initialInputs.length
      ? [
          {
            label: this.translocoService.translate('Photos déjà ajoutées'),
            items: this.initialInputs.map<MediaListItem>((input) =>
              input instanceof File ? { type: 'file', file: input } : { type: 'media', media: input },
            ),
          },
        ]
      : [],
  );

  constructor() {
    this.service.init(this.initialInputs);
  }

  protected onArucoDetected(item: MediaListItem, detected: boolean): void {
    this.service.setDetected(item, detected);
  }

  protected onArucoSurface(item: MediaListItem, surface: number): void {
    this.service.setSurface(item, surface);
  }

  protected onUploadedFilesChange(files: File[]): void {
    this.service.registerUploadedFiles(files);
  }

  protected onConfirmed(selection: MediaPickerSelection): void {
    if (this.isConfirming()) {
      return;
    }
    const firstMedia = selection.medias[0];
    const firstFile = selection.files[0];
    if (!firstMedia && !firstFile) {
      return;
    }
    this.isConfirming.set(true);
    if (firstMedia) {
      this.confirmMedia(firstMedia);
      return;
    }
    this.confirmFile(firstFile);
  }

  protected onCancelled(): void {
    this.context.completeWith(null);
  }

  private confirmFile(file: File): void {
    const picture = this.service.get({ type: 'file', file });
    if (!picture) {
      this.isConfirming.set(false);
      this.alertService
        .open(this.translocoService.translate('Échec de la détection du sticker ArUco'), { appearance: 'negative' })
        .subscribe();
      return;
    }
    const exists = this.initialInputs.some(
      (input) => input instanceof File && input.name === file.name && input.size === file.size,
    );
    this.context.completeWith(toPictureProviderOutput(picture, exists));
  }

  // The surface isn't persisted on saved medias, so detect it on demand for the single chosen one.
  // Mirrors woundtrack-quick-inputs: complete with the detected picture; on failure, close with null.
  private confirmMedia(media: Media): void {
    const exists = this.initialInputs.some((input) => !(input instanceof File) && (input as Media).id === media.id);
    this.arucoDetectionService
      .getArucoSticker$(media.uri)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          console.error('ArUco detection failed:', error);
          this.alertService
            .open(this.translocoService.translate('Échec de la détection du sticker ArUco'), { appearance: 'negative' })
            .subscribe();
          this.context.completeWith(null);
          return EMPTY;
        }),
      )
      .subscribe((sticker: ArucoSticker) =>
        this.context.completeWith(toPictureProviderOutput(toArucoPicture(media, sticker), exists)),
      );
  }
}
